<script setup lang="ts">
import { useMediaQuery } from '@vueuse/core';
import { computed, nextTick, ref, useId, watch } from 'vue';

import type { Temporal } from '@shared/lib/date-time';

import { moveFocusFromTrigger, plural } from '@/shared/lib';
import { AppButton, AppSelect, BaseDropdown, IconCalendar, IconChevronDown } from '@/shared/ui';
import { parsePlainDate } from '@shared/lib/date-time';

import type { DateRange } from './date-range';

import { dateRangePresets, formatDateRange, getDateRangeError } from './date-range';
import DateRangeFields from './DateRangeFields.vue';

interface Props {
  dateFrom: string;
  dateTo: string;
  /** Сегодня в поясе организации: от него считаются быстрые периоды. */
  today: Temporal.PlainDate | null;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  apply: [range: DateRange];
}>();

const triggerRef = ref<HTMLButtonElement | null>(null);
const popoverRef = ref<HTMLFormElement | null>(null);
const draftDateFrom = ref(props.dateFrom);
const draftDateTo = ref(props.dateTo);
const popoverId = `date-range-${useId()}`;
const errorId = `${popoverId}-error`;

watch(
  () => [props.dateFrom, props.dateTo] as const,
  ([dateFrom, dateTo]) => {
    draftDateFrom.value = dateFrom;
    draftDateTo.value = dateTo;
  },
);

const draftError = computed<string | null>(() => getDateRangeError(draftDateFrom.value, draftDateTo.value));
const draftDays = computed(() => {
  const from = parsePlainDate(draftDateFrom.value);
  const to = parsePlainDate(draftDateTo.value);
  if (!from || !to || draftError.value) return '';

  const days = from.until(to).days + 1;
  return `${days} ${plural(days, ['день', 'дня', 'дней'])}`;
});

// Два месяца, прошлый и текущий, когда окну есть где развернуться.
const isWide = useMediaQuery('(min-width: 768px)');

const hasAppliedRange = computed(() => Boolean(props.dateFrom || props.dateTo));
const hasDraftRange = computed(() => Boolean(draftDateFrom.value || draftDateTo.value));

const triggerLabel = computed(() =>
  hasAppliedRange.value ? `Период: ${formatDateRange(props.dateFrom, props.dateTo)}` : 'Период',
);

const presets = computed(() => (props.today ? dateRangePresets(props.today) : []));
const activePreset = computed(
  () =>
    presets.value.find(({ range }) => range.dateFrom === draftDateFrom.value && range.dateTo === draftDateTo.value)
      ?.value ?? null,
);

// Фокус на окне, а не на поле: на телефоне поле открыло бы клавиатуру поверх календаря.
const focusPopover = (): void => {
  void nextTick(() => {
    requestAnimationFrame(() => popoverRef.value?.focus());
  });
};

const handleTriggerClick = (toggle: () => void, isOpen: boolean): void => {
  toggle();
  if (!isOpen) {
    focusPopover();
  }
};

const handleTriggerEscape = (toggle: () => void, isOpen: boolean): void => {
  if (!isOpen) {
    return;
  }

  toggle();
  void nextTick(() => triggerRef.value?.focus());
};

const applyRange = (close: () => void): void => {
  if (draftError.value) {
    return;
  }

  emit('apply', {
    dateFrom: draftDateFrom.value,
    dateTo: draftDateTo.value,
  });
  close();
  void nextTick(() => triggerRef.value?.focus());
};

const applyPreset = (value: unknown, close: () => void): void => {
  const preset = presets.value.find((item) => item.value === value);
  if (!preset) return;

  draftDateFrom.value = preset.range.dateFrom;
  draftDateTo.value = preset.range.dateTo;
  applyRange(close);
};

const clearRange = (close: () => void): void => {
  draftDateFrom.value = '';
  draftDateTo.value = '';
  emit('apply', { dateFrom: '', dateTo: '' });
  close();
  void nextTick(() => triggerRef.value?.focus());
};

const handlePopoverKeydown = (event: KeyboardEvent, close: () => void): void => {
  if (event.key === 'Escape') {
    event.preventDefault();
    close();
    void nextTick(() => triggerRef.value?.focus());
    return;
  }

  if (event.key !== 'Tab' || !popoverRef.value) {
    return;
  }

  const focusableElements = Array.from(
    popoverRef.value.querySelectorAll<HTMLElement>('button:not([disabled]), input:not([disabled])'),
  );
  const currentIndex = focusableElements.indexOf(document.activeElement as HTMLElement);
  const isLeavingBackward = event.shiftKey && currentIndex <= 0;
  const isLeavingForward = !event.shiftKey && currentIndex === focusableElements.length - 1;

  if (!isLeavingBackward && !isLeavingForward) {
    return;
  }

  event.preventDefault();
  close();
  void nextTick(() => moveFocusFromTrigger(triggerRef.value, event.shiftKey));
};
</script>

<template>
  <BaseDropdown
    :width="isWide ? 'min(640px, calc(100vw - 2rem))' : 'min(320px, calc(100vw - 2rem))'"
    trigger-class="w-full sm:w-auto"
    :content="{ align: 'start', side: 'bottom', sideOffset: 8 }"
  >
    <template #trigger="{ isOpen, toggle }">
      <button
        ref="triggerRef"
        type="button"
        class="flex h-11 w-full cursor-pointer items-center gap-2 rounded-lg border border-(--field-border) bg-(--field-bg) px-4 text-sm text-(--text-primary) transition-[background-color,box-shadow,transform] duration-150 hover:bg-(--surface-hover) focus-visible:ring-2 focus-visible:ring-(--accent) focus-visible:outline-none active:scale-[0.96] sm:w-auto"
        aria-haspopup="dialog"
        :aria-expanded="isOpen"
        :aria-controls="popoverId"
        @click="handleTriggerClick(toggle, isOpen)"
        @keydown.esc.prevent="handleTriggerEscape(toggle, isOpen)"
      >
        <IconCalendar
          aria-hidden="true"
          class="shrink-0"
          :width="16"
          :height="16"
        />
        <span class="min-w-0 flex-1 truncate text-left sm:flex-none">{{ triggerLabel }}</span>
        <IconChevronDown
          aria-hidden="true"
          class="shrink-0 transition-transform duration-150"
          :class="{ 'rotate-180': isOpen }"
          :width="10"
          :height="10"
        />
      </button>
    </template>

    <template #content="{ close }">
      <form
        :id="popoverId"
        ref="popoverRef"
        role="dialog"
        aria-label="Период"
        tabindex="-1"
        class="flex flex-col gap-3 p-3 outline-none"
        @submit.prevent="applyRange(close)"
        @keydown="handlePopoverKeydown($event, close)"
      >
        <DateRangeFields
          v-model:date-from="draftDateFrom"
          v-model:date-to="draftDateTo"
          :error="draftError"
          :today="today"
          :error-id="errorId"
          :months="isWide ? 2 : 1"
        >
          <template
            v-if="presets.length > 0"
            #presets
          >
            <AppSelect
              :model-value="activePreset"
              :options="presets"
              :clearable="false"
              placeholder="Свой период"
              aria-label="Быстрый период"
              @update:model-value="applyPreset($event, close)"
            />
          </template>
        </DateRangeFields>

        <p
          v-if="draftError"
          :id="errorId"
          role="alert"
          class="text-sm text-(--danger)"
        >
          {{ draftError }}
        </p>

        <div class="flex flex-wrap items-center gap-2">
          <span class="mr-auto text-sm text-(--text-secondary)">{{ draftDays }}</span>
          <AppButton
            v-if="hasDraftRange"
            type="button"
            outline
            class="min-h-11"
            @click="clearRange(close)"
          >
            Сбросить
          </AppButton>
          <AppButton
            type="submit"
            class="min-h-11"
            :disabled="Boolean(draftError)"
          >
            Применить
          </AppButton>
        </div>
      </form>
    </template>
  </BaseDropdown>
</template>
